import EventEmitter3 from 'eventemitter3';
import { stableStringify } from '@epihub/core/src/fn/stableStringify';
import { safely } from '@epihub/core/src/fn/safely';

export type EventCreator<
  Data extends object,
  Key extends object,
> = {
  TData: Data;
  name: string;
  dataToKey: (data: Data) => Key;
};

export type EventCreatorWithKey<
  Data extends object,
  Key extends object,
> = EventCreator<Data, Key> & {
  key: string;
};

interface EventBuilder<
  Data extends object,
  Key extends object,
> {
  (key: Key): EventCreatorWithKey<Data, Key>;
  TTypes: {
    Data: Data;
    Key: Key;
  };
  dataToKey: (data: Data) => Key;
  keyCreator: (key: Key) => string;
  isEventKey: (key: string) => Key | null;
}

export class Emitter extends EventEmitter3<any, any> {
  static event<Data extends object>(name: string) {
    return function<Key extends object>(dataToKey: (data: Data) => Key) {
      const __NAME_PAYLOAD_SEPARATOR__ = '-';

      function keyCreator(key: Key) {
        return `${name}${__NAME_PAYLOAD_SEPARATOR__}${stableStringify(key)}`;
      }

      const func = function(key: Key) {
        return {
          TData: null as unknown as Data,
          name,
          dataToKey,
          key: keyCreator(key),
        } satisfies EventCreatorWithKey<Data, Key>;
      } as EventBuilder<Data, Key>;

      function isEventKey(key: string) {
        const [_name, ...json] = key.split(__NAME_PAYLOAD_SEPARATOR__);
        if (!key.startsWith(name)) {
          return null;
        }

        const parsed = safely(
          () => JSON.parse(json.join(__NAME_PAYLOAD_SEPARATOR__)),
          () => false,
        );

        if (!parsed) {
          return null;
        }

        return parsed as Key;
      }

      func.dataToKey = dataToKey;
      func.keyCreator = keyCreator;
      func.isEventKey = isEventKey;
      return func;
    };
  }

  readonly anyListeners = [] as Array<(event: string, ...args: any[]) => void>;

  constructor() {
    super();
    this.listen = this.listen.bind(this);
    this.send = this.send.bind(this);
    this.emit = this.emit.bind(this);
    this.onAny = this.onAny.bind(this);
    this.offAny = this.offAny.bind(this);
  }

  // Custom method to allow our own types
  // EventEmitter3 type aiPassthrough slows ts down too much
  listen<Event extends EventCreatorWithKey<any, any>>(
    event: Event,
    cb: (data: Event extends EventCreatorWithKey<infer Data, any> ? Data : never) => void,
  ) {
    this.on(event.key, cb);
    return {
      unsubscribe: () => {
        this.off(event, cb);
      },
    };
  }

  // Custom method to allow our own types
  // EventEmitter3 type aiPassthrough slows ts down too much
  send<
    Data extends object,
    Key extends object,
  >(
    event: EventBuilder<Data, Key>,
    data: Data,
  ) {
    this.emit(event.keyCreator(event.dataToKey(data)), data);
  }

  emit(event: string, ...args: any) {
    this.anyListeners.forEach((listener) => {
      listener(event, ...args);
    });

    return super.emit(event, ...args);
  }

  onAny(listener: (event: string, ...args: any) => any) {
    this.anyListeners.push(listener);
    return this;
  }

  offAny(listener) {
    const index = this.anyListeners.indexOf(listener);
    if (index !== -1) {
      this.anyListeners.splice(index, 1);
    }
    return this;
  }
}
